import { useAtom } from 'jotai';
import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { accessTokenAtom, userAtom, useGlobalActions } from '../../atoms';
import { paths } from '../../../config/paths';

interface Activity {
    id: string;
    type: string;
    status: string;
    related_user_path: string | null;
    wallet_chain: string | null;
    wallet_address: string | null;
    wallet_transaction_id: string | null;
    amount: number;
    created_at?: Date;
    updated_at?: Date;
}

const calculateTotalRevenue = (activities: Activity[]) => {
    return activities
        .filter(activity => activity.amount > 0 && ['affiliate_reward', 'withdraw_reward'].includes(activity.type))
        .reduce((sum, activity) => sum + activity.amount, 0);
};

const calculateAvailableBalance = (activities: Activity[]) => {
    const totalPositive = activities
        .filter(activity => activity.amount > 0 && activity.status === 'processed' && ['affiliate_reward', 'withdraw_reward'].includes(activity.type))
        .reduce((sum, activity) => sum + activity.amount, 0);

    const totalNegative = activities
        .filter(activity => activity.amount < 0 && ['affiliate_reward', 'withdraw_reward'].includes(activity.type))
        .reduce((sum, activity) => sum + activity.amount, 0);

    return totalPositive + totalNegative;
};

const AmountDisplay = ({ label, amount }: { label: string; amount: number }) => (
    <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
            type: "spring",
            stiffness: 260,
            damping: 20
        }}
        className="flex flex-col items-center justify-center p-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg"
    >
        <motion.h3
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            className="text-xl font-semibold text-white mb-2"
        >
            {label}
        </motion.h3>
        <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{
                type: "spring",
                stiffness: 300,
                damping: 10
            }}
            className="text-4xl font-bold text-white"
        >
            ${amount.toLocaleString()}
        </motion.div>
    </motion.div>
);

const ReferralSection = ({
    referralCode,
    distributorCode,
    showDistributorCode
}: {
    referralCode: string;
    distributorCode?: string | null;
    showDistributorCode: boolean;
}) => {
    const [showToast, setShowToast] = useState(false);
    const referralLink = `${window.location.origin}/register?ref=${referralCode}`;
    const distributorLink = distributorCode ? `${window.location.origin}/register?ref=${distributorCode}` : '';

    const copyToClipboard = useCallback(async (link: string) => {
        try {
            await navigator.clipboard.writeText(link);
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000); // 3秒後に非表示
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }
    }, []);

    return (
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6 space-y-4 relative"
        >
            {showToast && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg"
                >
                    Link copied to clipboard
                </motion.div>
            )}
            <h3 className="text-xl font-semibold text-gray-900">Referral Program</h3>

            <div className="space-y-4">
                <div className="space-y-2">
                    <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <span className="text-sm font-medium text-gray-600">Referral Code</span>
                        <span className="text-lg font-semibold text-gray-900">{referralCode}</span>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-600">Referral Link</label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={referralLink}
                                readOnly
                                className="flex-1 p-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                                onClick={() => copyToClipboard(referralLink)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                                Copy
                            </button>
                        </div>
                    </div>
                </div>

                {showDistributorCode && distributorCode && (
                    <div className="space-y-2 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                            <span className="text-sm font-medium text-gray-600">Distributor Code</span>
                            <span className="text-lg font-semibold text-gray-900">{distributorCode}</span>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-600">Distributor Link</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={distributorLink}
                                    readOnly
                                    className="flex-1 p-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                    onClick={() => copyToClipboard(distributorLink)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    Copy
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

const RewardTable = ({ assigned_reward_total }: { assigned_reward_total: number }) => {
    return (
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-6"
        >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Referral Reward</h3>
            <div className="flex flex-col gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-100 rounded-full">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">Referral Reward per children</p>
                            <p className="text-2xl font-bold text-gray-900">${Math.floor(assigned_reward_total).toLocaleString()}</p>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const DistributorTable = ({ rewards }: { rewards: Array<{ child_number: number; amount: number }> }) => {
    if (!rewards || rewards.length === 0) return null;

    return (
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-6"
        >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Distributor Rewards</h3>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Relative Depth</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reward</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {rewards.map((reward, index) => (
                            <motion.tr
                                key={reward.child_number}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    Level {reward.child_number}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                                    ${reward.amount.toLocaleString()}
                                </td>
                            </motion.tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </motion.div>
    );
};

const ActivityTable = ({ activities, currentUserId }: { activities: Activity[]; currentUserId: string }) => {
    const filteredActivities = activities.filter(
        activity => activity.type === 'affiliate_reward' || activity.type === 'withdraw_reward'
    );

    const TypeBadge = ({ type }: { type: string }) => {
        const isReward = type === 'affiliate_reward';
        return (
            <span className={`px-2 py-1 rounded text-white text-sm font-medium ${isReward ? 'bg-green-500' : 'bg-red-500'
                }`}>
                {isReward ? 'Reward' : 'Withdraw'}
            </span>
        );
    };

    const StatusBadge = ({ status }: { status: string }) => {
        const statusConfig = {
            waiting: { text: 'Waiting', className: 'bg-blue-100 text-blue-800' },
            checking: { text: 'Checking', className: 'bg-yellow-100 text-yellow-800' },
            processed: { text: 'Approved', className: 'bg-green-100 text-green-800' },
        }[status] || { text: status, className: 'bg-gray-100 text-gray-800' };

        return (
            <span className={`px-2 py-1 rounded text-sm font-medium ${statusConfig.className}`}>
                {statusConfig.text}
            </span>
        );
    };

    const RelatedUsers = ({ userPath, currentUserId }: { userPath: string | null; currentUserId: string }) => {
        if (!userPath) return null;
        const users = userPath.split('/').reverse();
        const currentUserIndex = users.findIndex(id => id === currentUserId);

        // 現在のユーザー以降のユーザーのみを表示
        const displayUsers = users.slice(currentUserIndex);

        return (
            <div className="space-y-1">
                {displayUsers.map((id, index) => (
                    <div
                        key={index}
                        className={`text-sm ${id === currentUserId
                            ? 'text-red-600 font-bold'
                            : 'text-gray-600'
                            }`}
                    >
                        {`${index + 1}. ${id.slice(0, 5)}...`}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-lg p-6"
        >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Transaction History</h3>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Related Users</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredActivities.reverse().map((activity, index) => (
                            <motion.tr
                                key={activity.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <TypeBadge type={activity.type} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <StatusBadge status={activity.status} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <RelatedUsers userPath={activity.related_user_path} currentUserId={currentUserId} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                                    ${activity.amount.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        {activity.created_at?.toString()}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {activity.updated_at?.toString()}
                                    </div>
                                </td>
                            </motion.tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </motion.div>
    );
};

const WithdrawSection = ({ activities }: { activities: Activity[] }) => {
    const [accessToken] = useAtom(accessTokenAtom);
    const [user] = useAtom(userAtom);
    const { userChallenge } = useGlobalActions();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [chain, setChain] = useState<string>(user?.default_withdraw_address_chain || '');
    const [walletAddress, setWalletAddress] = useState<string>(user?.default_withdraw_address || '');
    const availableBalance = calculateAvailableBalance(activities);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!accessToken || isSubmitting) return;

        setIsSubmitting(true);
        setError(null);
        try {
            const response = await fetch('/api/v1/users/withdraw', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    wallet_chain: chain,
                    wallet_address: walletAddress
                })
            });

            if (!response.ok) {
                const data = await response.json() as { message: string };
                throw new Error(data.message || 'Withdrawal request failed');
            }

            // userAtomを更新
            await userChallenge();
        } catch (error) {
            console.error('Error submitting withdrawal request:', error);
            setError(error instanceof Error ? error.message : 'An error occurred while processing your request');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl shadow-lg p-6"
        >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Withdrawal Request</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="chain" className="block text-sm font-medium text-gray-700 mb-1">
                        Chain
                    </label>
                    <select
                        id="chain"
                        value={chain}
                        onChange={(e) => setChain(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                    >
                        <option value="">Please select</option>
                        <option value="BEP20">BEP20</option>
                        <option value="TRC20">TRC20</option>
                    </select>
                </div>

                <div>
                    <label htmlFor="walletAddress" className="block text-sm font-medium text-gray-700 mb-1">
                        Wallet Address
                    </label>
                    <input
                        type="text"
                        id="walletAddress"
                        value={walletAddress}
                        onChange={(e) => setWalletAddress(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                    />
                </div>

                {error && (
                    <div className="text-red-600 text-sm">
                        {error}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={availableBalance <= 0 || !chain || !walletAddress || isSubmitting}
                    className={`w-full py-3 px-4 rounded-md text-white font-medium ${availableBalance <= 0 || !chain || !walletAddress || isSubmitting
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 transition-colors duration-200'
                        }`}
                >
                    {isSubmitting ? (
                        <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                        </span>
                    ) : (
                        `Claim all balance ($${availableBalance.toLocaleString()})`
                    )}
                </button>
            </form>
        </motion.div>
    );
};

export const Dashboard = () => {
    const navigate = useNavigate();
    const [accessToken] = useAtom(accessTokenAtom);
    const [user] = useAtom(userAtom);
    const { userChallenge } = useGlobalActions();

    // 住所編集用の状態
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [isSubmittingAddress, setIsSubmittingAddress] = useState(false);
    const [addressError, setAddressError] = useState<string | null>(null);
    const [addressForm, setAddressForm] = useState({
        country: '',
        postal_code: '',
        address1: '',
        address2: ''
    });

    // 住所フォームの初期化
    useEffect(() => {
        if (user) {
            setAddressForm({
                country: user.country || '',
                postal_code: user.postal_code || '',
                address1: user.address1 || '',
                address2: user.address2 || ''
            });
        }
    }, [user]);

    // 住所更新処理
    const handleAddressSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!accessToken || isSubmittingAddress) return;

        setIsSubmittingAddress(true);
        setAddressError(null);

        try {
            const response = await fetch('/api/v1/users/update-address', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    country: addressForm.country,
                    postal_code: addressForm.postal_code,
                    address1: addressForm.address1,
                    address2: addressForm.address2 || undefined
                })
            });

            if (!response.ok) {
                const data = await response.json() as { message: string };
                throw new Error(data.message || 'Failed to update address');
            }

            // 更新成功
            await userChallenge(); // ユーザー情報を再取得
            setIsEditingAddress(false);
        } catch (error) {
            console.error('Error updating address:', error);
            setAddressError(error instanceof Error ? error.message : 'An error occurred while updating your address');
        } finally {
            setIsSubmittingAddress(false);
        }
    };

    // 編集キャンセル
    const cancelAddressEdit = () => {
        setIsEditingAddress(false);
        setAddressError(null);
        // フォームを現在のユーザー情報に戻す
        if (user) {
            setAddressForm({
                country: user.country || '',
                postal_code: user.postal_code || '',
                address1: user.address1 || '',
                address2: user.address2 || ''
            });
        }
    };

    useEffect(() => {
        if (!accessToken) {
            navigate(paths.auth.login.path);
            return;
        }
        userChallenge();
    }, [accessToken, navigate, userChallenge]);

    useEffect(() => {
        if (!user) {
            return;
        }
        if (!user.is_debit_card_purchased) {
            navigate(paths.buyCard.path);
            return;
        }
    }, [user, navigate]);

    if (!user) {
        return null;
    }

    const totalRevenue = calculateTotalRevenue(user.activities);
    const currentBalance = calculateAvailableBalance(user.activities);

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-8">
                <motion.div
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    className="text-center mb-12"
                >
                    <h2 className="text-4xl font-extrabold text-gray-900 mb-4">
                        Welcome, {user.username}
                    </h2>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <AmountDisplay label="Total Revenue" amount={totalRevenue} />
                    <AmountDisplay label="Current Balance" amount={currentBalance} />
                </div>

                <ReferralSection
                    referralCode={user.debit_referral_code || ''}
                    distributorCode={user.debit_distributor_code}
                    showDistributorCode={user.assigned_reward_total > 0 && user.reward_trees && user.reward_trees.length > 0}
                />

                {user.assigned_reward_total > 0 && (
                    <RewardTable assigned_reward_total={user.assigned_reward_total} />
                )}

                {user.reward_trees && user.reward_trees.length > 0 && (
                    <DistributorTable rewards={user.reward_trees} />
                )}

                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white rounded-xl shadow-lg p-6"
                >
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold text-gray-900">Shipping Address</h3>
                        {!isEditingAddress && (
                            <button
                                onClick={() => setIsEditingAddress(true)}
                                className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                                Edit
                            </button>
                        )}
                    </div>

                    {isEditingAddress ? (
                        <form onSubmit={handleAddressSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                                        Country
                                    </label>
                                    <select
                                        id="country"
                                        value={addressForm.country}
                                        onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    >
                                        <option value="">Select Country</option>
                                        <option value="Afganistan">Afghanistan</option>
                                        <option value="Albania">Albania</option>
                                        <option value="Algeria">Algeria</option>
                                        <option value="American Samoa">American Samoa</option>
                                        <option value="Andorra">Andorra</option>
                                        <option value="Angola">Angola</option>
                                        <option value="Anguilla">Anguilla</option>
                                        <option value="Antigua &amp; Barbuda">Antigua &amp; Barbuda</option>
                                        <option value="Argentina">Argentina</option>
                                        <option value="Armenia">Armenia</option>
                                        <option value="Aruba">Aruba</option>
                                        <option value="Australia">Australia</option>
                                        <option value="Austria">Austria</option>
                                        <option value="Azerbaijan">Azerbaijan</option>
                                        <option value="Bahamas">Bahamas</option>
                                        <option value="Bahrain">Bahrain</option>
                                        <option value="Bangladesh">Bangladesh</option>
                                        <option value="Barbados">Barbados</option>
                                        <option value="Belarus">Belarus</option>
                                        <option value="Belgium">Belgium</option>
                                        <option value="Belize">Belize</option>
                                        <option value="Benin">Benin</option>
                                        <option value="Bermuda">Bermuda</option>
                                        <option value="Bhutan">Bhutan</option>
                                        <option value="Bolivia">Bolivia</option>
                                        <option value="Bonaire">Bonaire</option>
                                        <option value="Bosnia &amp; Herzegovina">Bosnia &amp; Herzegovina</option>
                                        <option value="Botswana">Botswana</option>
                                        <option value="Brazil">Brazil</option>
                                        <option value="British Indian Ocean Ter">British Indian Ocean Ter</option>
                                        <option value="Brunei">Brunei</option>
                                        <option value="Bulgaria">Bulgaria</option>
                                        <option value="Burkina Faso">Burkina Faso</option>
                                        <option value="Burundi">Burundi</option>
                                        <option value="Cambodia">Cambodia</option>
                                        <option value="Cameroon">Cameroon</option>
                                        <option value="Canada">Canada</option>
                                        <option value="Canary Islands">Canary Islands</option>
                                        <option value="Cape Verde">Cape Verde</option>
                                        <option value="Cayman Islands">Cayman Islands</option>
                                        <option value="Central African Republic">Central African Republic</option>
                                        <option value="Chad">Chad</option>
                                        <option value="Channel Islands">Channel Islands</option>
                                        <option value="Chile">Chile</option>
                                        <option value="China">China</option>
                                        <option value="Christmas Island">Christmas Island</option>
                                        <option value="Cocos Island">Cocos Island</option>
                                        <option value="Colombia">Colombia</option>
                                        <option value="Comoros">Comoros</option>
                                        <option value="Congo">Congo</option>
                                        <option value="Cook Islands">Cook Islands</option>
                                        <option value="Costa Rica">Costa Rica</option>
                                        <option value="Cote DIvoire">Cote D'Ivoire</option>
                                        <option value="Croatia">Croatia</option>
                                        <option value="Cuba">Cuba</option>
                                        <option value="Curaco">Curacao</option>
                                        <option value="Cyprus">Cyprus</option>
                                        <option value="Czech Republic">Czech Republic</option>
                                        <option value="Denmark">Denmark</option>
                                        <option value="Djibouti">Djibouti</option>
                                        <option value="Dominica">Dominica</option>
                                        <option value="Dominican Republic">Dominican Republic</option>
                                        <option value="East Timor">East Timor</option>
                                        <option value="Ecuador">Ecuador</option>
                                        <option value="Egypt">Egypt</option>
                                        <option value="El Salvador">El Salvador</option>
                                        <option value="Equatorial Guinea">Equatorial Guinea</option>
                                        <option value="Eritrea">Eritrea</option>
                                        <option value="Estonia">Estonia</option>
                                        <option value="Ethiopia">Ethiopia</option>
                                        <option value="Falkland Islands">Falkland Islands</option>
                                        <option value="Faroe Islands">Faroe Islands</option>
                                        <option value="Fiji">Fiji</option>
                                        <option value="Finland">Finland</option>
                                        <option value="France">France</option>
                                        <option value="French Guiana">French Guiana</option>
                                        <option value="French Polynesia">French Polynesia</option>
                                        <option value="French Southern Ter">French Southern Ter</option>
                                        <option value="Gabon">Gabon</option>
                                        <option value="Gambia">Gambia</option>
                                        <option value="Georgia">Georgia</option>
                                        <option value="Germany">Germany</option>
                                        <option value="Ghana">Ghana</option>
                                        <option value="Gibraltar">Gibraltar</option>
                                        <option value="Great Britain">Great Britain</option>
                                        <option value="Greece">Greece</option>
                                        <option value="Greenland">Greenland</option>
                                        <option value="Grenada">Grenada</option>
                                        <option value="Guadeloupe">Guadeloupe</option>
                                        <option value="Guam">Guam</option>
                                        <option value="Guatemala">Guatemala</option>
                                        <option value="Guinea">Guinea</option>
                                        <option value="Guyana">Guyana</option>
                                        <option value="Haiti">Haiti</option>
                                        <option value="Hawaii">Hawaii</option>
                                        <option value="Honduras">Honduras</option>
                                        <option value="Hong Kong">Hong Kong</option>
                                        <option value="Hungary">Hungary</option>
                                        <option value="Iceland">Iceland</option>
                                        <option value="India">India</option>
                                        <option value="Indonesia">Indonesia</option>
                                        <option value="Iran">Iran</option>
                                        <option value="Iraq">Iraq</option>
                                        <option value="Ireland">Ireland</option>
                                        <option value="Isle of Man">Isle of Man</option>
                                        <option value="Israel">Israel</option>
                                        <option value="Italy">Italy</option>
                                        <option value="Jamaica">Jamaica</option>
                                        <option value="Japan">Japan</option>
                                        <option value="Jordan">Jordan</option>
                                        <option value="Kazakhstan">Kazakhstan</option>
                                        <option value="Kenya">Kenya</option>
                                        <option value="Kiribati">Kiribati</option>
                                        <option value="Korea North">Korea North</option>
                                        <option value="Korea Sout">Korea South</option>
                                        <option value="Kuwait">Kuwait</option>
                                        <option value="Kyrgyzstan">Kyrgyzstan</option>
                                        <option value="Laos">Laos</option>
                                        <option value="Latvia">Latvia</option>
                                        <option value="Lebanon">Lebanon</option>
                                        <option value="Lesotho">Lesotho</option>
                                        <option value="Liberia">Liberia</option>
                                        <option value="Libya">Libya</option>
                                        <option value="Liechtenstein">Liechtenstein</option>
                                        <option value="Lithuania">Lithuania</option>
                                        <option value="Luxembourg">Luxembourg</option>
                                        <option value="Macau">Macau</option>
                                        <option value="Macedonia">Macedonia</option>
                                        <option value="Madagascar">Madagascar</option>
                                        <option value="Malaysia">Malaysia</option>
                                        <option value="Malawi">Malawi</option>
                                        <option value="Maldives">Maldives</option>
                                        <option value="Mali">Mali</option>
                                        <option value="Malta">Malta</option>
                                        <option value="Marshall Islands">Marshall Islands</option>
                                        <option value="Martinique">Martinique</option>
                                        <option value="Mauritania">Mauritania</option>
                                        <option value="Mauritius">Mauritius</option>
                                        <option value="Mayotte">Mayotte</option>
                                        <option value="Mexico">Mexico</option>
                                        <option value="Midway Islands">Midway Islands</option>
                                        <option value="Moldova">Moldova</option>
                                        <option value="Monaco">Monaco</option>
                                        <option value="Mongolia">Mongolia</option>
                                        <option value="Montserrat">Montserrat</option>
                                        <option value="Morocco">Morocco</option>
                                        <option value="Mozambique">Mozambique</option>
                                        <option value="Myanmar">Myanmar</option>
                                        <option value="Nambia">Nambia</option>
                                        <option value="Nauru">Nauru</option>
                                        <option value="Nepal">Nepal</option>
                                        <option value="Netherland Antilles">Netherland Antilles</option>
                                        <option value="Netherlands">Netherlands (Holland, Europe)</option>
                                        <option value="Nevis">Nevis</option>
                                        <option value="New Caledonia">New Caledonia</option>
                                        <option value="New Zealand">New Zealand</option>
                                        <option value="Nicaragua">Nicaragua</option>
                                        <option value="Niger">Niger</option>
                                        <option value="Nigeria">Nigeria</option>
                                        <option value="Niue">Niue</option>
                                        <option value="Norfolk Island">Norfolk Island</option>
                                        <option value="Norway">Norway</option>
                                        <option value="Oman">Oman</option>
                                        <option value="Pakistan">Pakistan</option>
                                        <option value="Palau Island">Palau Island</option>
                                        <option value="Palestine">Palestine</option>
                                        <option value="Panama">Panama</option>
                                        <option value="Papua New Guinea">Papua New Guinea</option>
                                        <option value="Paraguay">Paraguay</option>
                                        <option value="Peru">Peru</option>
                                        <option value="Phillipines">Philippines</option>
                                        <option value="Pitcairn Island">Pitcairn Island</option>
                                        <option value="Poland">Poland</option>
                                        <option value="Portugal">Portugal</option>
                                        <option value="Puerto Rico">Puerto Rico</option>
                                        <option value="Qatar">Qatar</option>
                                        <option value="Republic of Montenegro">Republic of Montenegro</option>
                                        <option value="Republic of Serbia">Republic of Serbia</option>
                                        <option value="Reunion">Reunion</option>
                                        <option value="Romania">Romania</option>
                                        <option value="Russia">Russia</option>
                                        <option value="Rwanda">Rwanda</option>
                                        <option value="St Barthelemy">St Barthelemy</option>
                                        <option value="St Eustatius">St Eustatius</option>
                                        <option value="St Helena">St Helena</option>
                                        <option value="St Kitts-Nevis">St Kitts-Nevis</option>
                                        <option value="St Lucia">St Lucia</option>
                                        <option value="St Maarten">St Maarten</option>
                                        <option value="St Pierre &amp; Miquelon">St Pierre &amp; Miquelon</option>
                                        <option value="St Vincent &amp; Grenadines">St Vincent &amp; Grenadines</option>
                                        <option value="Saipan">Saipan</option>
                                        <option value="Samoa">Samoa</option>
                                        <option value="Samoa American">Samoa American</option>
                                        <option value="San Marino">San Marino</option>
                                        <option value="Sao Tome &amp; Principe">Sao Tome &amp; Principe</option>
                                        <option value="Saudi Arabia">Saudi Arabia</option>
                                        <option value="Senegal">Senegal</option>
                                        <option value="Serbia">Serbia</option>
                                        <option value="Seychelles">Seychelles</option>
                                        <option value="Sierra Leone">Sierra Leone</option>
                                        <option value="Singapore">Singapore</option>
                                        <option value="Slovakia">Slovakia</option>
                                        <option value="Slovenia">Slovenia</option>
                                        <option value="Solomon Islands">Solomon Islands</option>
                                        <option value="Somalia">Somalia</option>
                                        <option value="South Africa">South Africa</option>
                                        <option value="Spain">Spain</option>
                                        <option value="Sri Lanka">Sri Lanka</option>
                                        <option value="Sudan">Sudan</option>
                                        <option value="Suriname">Suriname</option>
                                        <option value="Swaziland">Swaziland</option>
                                        <option value="Sweden">Sweden</option>
                                        <option value="Switzerland">Switzerland</option>
                                        <option value="Syria">Syria</option>
                                        <option value="Tahiti">Tahiti</option>
                                        <option value="Taiwan">Taiwan</option>
                                        <option value="Tajikistan">Tajikistan</option>
                                        <option value="Tanzania">Tanzania</option>
                                        <option value="Thailand">Thailand</option>
                                        <option value="Togo">Togo</option>
                                        <option value="Tokelau">Tokelau</option>
                                        <option value="Tonga">Tonga</option>
                                        <option value="Trinidad &amp; Tobago">Trinidad &amp; Tobago</option>
                                        <option value="Tunisia">Tunisia</option>
                                        <option value="Turkey">Turkey</option>
                                        <option value="Turkmenistan">Turkmenistan</option>
                                        <option value="Turks &amp; Caicos Is">Turks &amp; Caicos Is</option>
                                        <option value="Tuvalu">Tuvalu</option>
                                        <option value="Uganda">Uganda</option>
                                        <option value="Ukraine">Ukraine</option>
                                        <option value="United Arab Erimates">United Arab Emirates</option>
                                        <option value="United Kingdom">United Kingdom</option>
                                        <option value="United States of America">United States of America</option>
                                        <option value="Uraguay">Uruguay</option>
                                        <option value="Uzbekistan">Uzbekistan</option>
                                        <option value="Vanuatu">Vanuatu</option>
                                        <option value="Vatican City State">Vatican City State</option>
                                        <option value="Venezuela">Venezuela</option>
                                        <option value="Vietnam">Vietnam</option>
                                        <option value="Virgin Islands (Brit)">Virgin Islands (Brit)</option>
                                        <option value="Virgin Islands (USA)">Virgin Islands (USA)</option>
                                        <option value="Wake Island">Wake Island</option>
                                        <option value="Wallis &amp; Futana Is">Wallis &amp; Futana Is</option>
                                        <option value="Yemen">Yemen</option>
                                        <option value="Zaire">Zaire</option>
                                        <option value="Zambia">Zambia</option>
                                        <option value="Zimbabwe">Zimbabwe</option>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                                        Postal Code
                                    </label>
                                    <input
                                        type="text"
                                        id="postalCode"
                                        value={addressForm.postal_code}
                                        onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="address1" className="block text-sm font-medium text-gray-700 mb-1">
                                    Address Line 1
                                </label>
                                <input
                                    type="text"
                                    id="address1"
                                    value={addressForm.address1}
                                    onChange={(e) => setAddressForm({ ...addressForm, address1: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="address2" className="block text-sm font-medium text-gray-700 mb-1">
                                    Address Line 2
                                </label>
                                <input
                                    type="text"
                                    id="address2"
                                    value={addressForm.address2 || ''}
                                    onChange={(e) => setAddressForm({ ...addressForm, address2: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {addressError && (
                                <div className="text-red-600 text-sm">
                                    {addressError}
                                </div>
                            )}

                            <div className="flex justify-end space-x-3 mt-4">
                                <button
                                    type="button"
                                    onClick={cancelAddressEdit}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmittingAddress}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                >
                                    {isSubmittingAddress ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Country
                                    </label>
                                    <div className="text-gray-900">{user.country || 'Not set'}</div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Postal Code
                                    </label>
                                    <div className="text-gray-900">{user.postal_code || 'Not set'}</div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address Line 1
                                </label>
                                <div className="text-gray-900">{user.address1 || 'Not set'}</div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address Line 2
                                </label>
                                <div className="text-gray-900">{user.address2 || 'Not set'}</div>
                            </div>
                        </div>
                    )}
                </motion.div>

                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="bg-white rounded-xl shadow-lg p-6"
                >
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Debit Card Activities</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {user.activities
                                    .filter(activity =>
                                        activity.type === 'purchase_debit_card' ||
                                        activity.type === 'deliver_debit_card'
                                    )
                                    .reverse()
                                    .map((activity, index) => (
                                        <motion.tr
                                            key={activity.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 rounded text-white text-sm font-medium ${activity.type === 'purchase_debit_card' ? 'bg-blue-500' : 'bg-green-500'
                                                    }`}>
                                                    {activity.type === 'purchase_debit_card' ? 'Purchase' : 'Delivery'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 rounded text-sm font-medium ${activity.status === 'processed' ? 'bg-green-100 text-green-800' :
                                                    activity.status === 'waiting' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {activity.status === 'processed' ? 'Completed' :
                                                        activity.status === 'waiting' ? 'Pending' : 'In Process'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {activity.created_at?.toLocaleString()}
                                            </td>
                                        </motion.tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </motion.div>

                <WithdrawSection activities={user.activities} />

                <ActivityTable activities={user.activities} currentUserId={user.id} />
            </div>
        </div>
    );
};